import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Skeleton, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { fetchProjectCreationFlow, configureProjectCreation, projectCreationFlowQueryKey, regenerateProjectCreationPlans, runProjectCreationTask, cancelProjectCreationTask, } from '@/api/projectApi';
import { notifyApiError } from '@/shared/api/httpClient';
import '@/features/create-project/ui/styles/creation-plan-picker.css';
const contentSchema = z.object({
    summary: z.string().optional(),
    worldview: z.string().optional(),
    outline: z
        .array(z.object({ title: z.string().optional(), summary: z.string().optional() }))
        .optional(),
});
/** 草稿方案选择器，轮询复用现有任务接口，不触发正式项目创建。 */
export function CreationPlanPicker(props) {
    const client = useQueryClient();
    const key = projectCreationFlowQueryKey(props.projectId);
    const query = useQuery({
        queryKey: key,
        queryFn: () => fetchProjectCreationFlow(props.projectId),
        refetchInterval: (state) => state.state.data?.tasks.some((task) => task.status === 0 || task.status === 1) ? 2500 : false,
    });
    const [autoStarting, setAutoStarting] = useState(props.autoStartTaskId !== undefined);
    const startedRef = useRef(false);
    const [acting, setBusy] = useState(false);
    const busy = acting || autoStarting;
    const { autoStartTaskId, onAutoStart } = props;
    // 首次进入配置即启动任务；关闭或重开只恢复进度，不重新扣费。
    useEffect(() => {
        if (autoStartTaskId === undefined || startedRef.current)
            return;
        startedRef.current = true;
        onAutoStart?.();
        void (async () => {
            try {
                await runProjectCreationTask(autoStartTaskId);
            }
            catch (error) {
                notifyApiError(error, { fallback: '方案生成失败，请重试' });
            }
            finally {
                await client.invalidateQueries({ queryKey: projectCreationFlowQueryKey(props.projectId) });
                setAutoStarting(false);
            }
        })();
    }, [autoStartTaskId, onAutoStart, client, props.projectId]);
    const plans = [...(query.data?.plans ?? [])]
        .sort((a, b) => b.id - a.id)
        .filter((plan, index, all) => all.findIndex((item) => item.planCode === plan.planCode) === index)
        .sort((a, b) => a.planCode.localeCompare(b.planCode));
    const active = plans.find((plan) => plan.id === props.value && plan.status === 2) ??
        plans.find((plan) => plan.status === 2);
    const defaultSelectionRef = useRef(null);
    const defaultPlanId = active?.status === 2 ? active.id : undefined;
    const { value, onChange } = props;
    useEffect(() => {
        if (defaultPlanId === undefined ||
            value === defaultPlanId ||
            defaultSelectionRef.current === `${String(value)}:${String(defaultPlanId)}`)
            return;
        defaultSelectionRef.current = `${String(value)}:${String(defaultPlanId)}`;
        onChange(defaultPlanId);
    }, [defaultPlanId, value, onChange]);
    let content = {};
    try {
        const parsed = contentSchema.safeParse(JSON.parse(active?.contentJson ?? '{}'));
        if (parsed.success)
            content = parsed.data;
    }
    catch {
        /* 生成中内容可能尚未形成完整 JSON。 */
    }
    const running = query.data?.tasks.find((task) => task.status === 0 || task.status === 1);
    const latestTask = [...(query.data?.tasks ?? [])].sort((a, b) => b.id - a.id).at(0);
    const failed = latestTask?.status === 3 ? latestTask : undefined;
    async function action(kind) {
        if (busy)
            return;
        setBusy(true);
        try {
            if (kind === 'cancel')
                await cancelProjectCreationTask(props.projectId);
            else if (kind === 'start' && running)
                await runProjectCreationTask(running.id);
            else if (kind === 'retry' && failed)
                await runProjectCreationTask(failed.id);
            else {
                const ids = active?.status === 2
                    ? [active.id]
                    : plans.filter((plan) => plan.status === 2).map((plan) => plan.id);
                const flow = ids.length === 0
                    ? await configureProjectCreation({
                        projectId: props.projectId,
                        creationMode: 'inspiration',
                        inspiration: props.inspiration,
                    })
                    : await regenerateProjectCreationPlans({ projectId: props.projectId, planIds: ids });
                const task = [...flow.tasks].sort((a, b) => b.id - a.id).find((item) => item.status === 0);
                if (task)
                    await runProjectCreationTask(task.id);
            }
            await client.invalidateQueries({ queryKey: key });
        }
        catch (error) {
            notifyApiError(error, { fallback: '方案操作失败，请重试' });
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("section", { className: "creation-plan-picker", "aria-label": "\u5267\u60C5\u65B9\u6848", children: [_jsx("h3", { children: "\u9009\u62E9\u5267\u60C5\u65B9\u6848" }), query.isPending ? (_jsx(Spin, {})) : query.isError ? (_jsx(Button, { onClick: () => {
                    void query.refetch();
                }, children: "\u65B9\u6848\u52A0\u8F7D\u5931\u8D25\uFF0C\u91CD\u8BD5" })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "creation-plan-picker__tabs", children: plans.map((plan) => (_jsxs(Button, { type: active?.id === plan.id ? 'primary' : 'default', disabled: plan.status !== 2 || Boolean(running) || busy, onClick: () => {
                                props.onChange(plan.id);
                            }, children: ["\u65B9\u6848 ", plan.planCode, plan.status === 0 ? ' · 生成中' : plan.status === 3 ? ' · 失败' : ''] }, plan.id))) }), active?.status === 2 && !running ? (_jsxs("div", { className: "creation-plan-picker__content", children: [_jsx("h4", { children: "\u5267\u60C5\u6982\u8981" }), _jsx("p", { children: content.summary }), _jsx("h4", { children: "\u4E16\u754C\u89C2" }), _jsx("p", { children: content.worldview }), _jsx("h4", { children: "\u5267\u60C5\u5927\u7EB2" }), _jsx("ol", { children: content.outline?.map((chapter, index) => (_jsxs("li", { children: [chapter.title, " ", chapter.summary] }, index))) })] })) : running || autoStarting ? (_jsxs("div", { className: "creation-plan-picker__generating", role: "status", "aria-live": "polite", children: [_jsxs("div", { children: [_jsx(Spin, { size: "small" }), " \u6B63\u5728\u521B\u4F5C\u5267\u60C5\u65B9\u6848\uFF0C\u4F60\u53EF\u4EE5\u5148\u586B\u5199\u9879\u76EE\u8BBE\u7F6E\u2026"] }), _jsx(Skeleton, { active: true, title: false, paragraph: { rows: 3 } })] })) : (_jsx("p", { role: "status", children: "\u9009\u62E9\u5267\u60C5\u65B9\u6848\u5E76\u586B\u5199\u9879\u76EE\u8BBE\u7F6E\uFF0C\u5373\u53EF\u751F\u6210\u5267\u672C\u3002" })), _jsxs("div", { className: "creation-plan-picker__actions", children: [running?.status === 0 && !autoStarting && (_jsx(Button, { loading: busy, onClick: () => {
                                    void action('start');
                                }, children: "\u5F00\u59CB\u751F\u6210 \u00B7 1 \u79EF\u5206" })), running ? (_jsx(Button, { loading: busy, onClick: () => {
                                    void action('cancel');
                                }, children: "\u53D6\u6D88\u751F\u6210" })) : (_jsx(Button, { loading: busy, onClick: () => {
                                    void action(failed ? 'retry' : 'regenerate');
                                }, children: failed ? '重试生成 · 1 积分' : '重新生成 · 1 积分' }))] }), query.data.creationError && _jsx("p", { role: "alert", children: "\u65B9\u6848\u751F\u6210\u5931\u8D25\uFF0C\u8BF7\u91CD\u8BD5\u3002" })] }))] }));
}
